import {
    AccessType,
    AppliesTo,
    buildOutboundTargets,
    CrossTenantAccessSettingsSchema,
    CrossTenantTarget,
    extractCrossTenantAccessSettings,
} from '../../../../../tools/schemas/graph/cross-tenant-access-settings.schema';
import {
    CrossTenantAccessPartition,
    CrossTenantAccessPolicyDefault,
    updateCrossTenantAccessPolicy,
} from 'projects/angular-clarity/src/app/stores/client/graph/cross-tenant-access-policy/actions';

const USER_TARGET = { target: 'AllUsers', targetType: 'user' };
const APP_TARGET = { target: 'AllApplications', targetType: 'application' };

/**
 * Build the cross-tenant access policy PATCH from the baseline schema.
 *
 * The baseline form keeps each direction's users-and-groups and applications access types
 * consistent (applications can never be more permissive than users-and-groups), so the values
 * read from the schema are written as-is. Existing target selections are preserved — and a
 * sensible "all users / all applications" default is supplied where none exists — so Microsoft
 * Graph accepts the partial update.
 */
export function Remediate(
    _tenant: string,
    current: CrossTenantAccessPolicyDefault,
    schema: CrossTenantAccessSettingsSchema,
) {
    const desired = extractCrossTenantAccessSettings(schema);

    const partition = (
        existing: CrossTenantAccessPartition | undefined,
        accessType: AccessType,
        defaultTarget: { target: string; targetType: string },
    ): CrossTenantAccessPartition => ({
        accessType,
        targets: existing?.targets ?? [defaultTarget],
    });

    // Outbound users-and-groups can target either all external users or a specific selection.
    // Use the targets built from the baseline selection so remediation writes exactly what was
    // chosen, falling back to the existing targets (then all users) when the baseline pins nothing.
    const outboundUsersPartition = (
        existing: CrossTenantAccessPartition | undefined,
        accessType: AccessType,
        targets: CrossTenantTarget[] | undefined,
    ): CrossTenantAccessPartition => ({
        accessType,
        targets: targets ?? existing?.targets ?? [USER_TARGET],
    });

    const outboundTargets = (
        appliesTo: AppliesTo | undefined,
        users: string[] | undefined,
        groups: string[] | undefined,
    ): CrossTenantTarget[] | undefined =>
        appliesTo === undefined ? undefined : buildOutboundTargets(appliesTo, users ?? [], groups ?? []);

    const updatedPolicy: Partial<CrossTenantAccessPolicyDefault> = {
        inboundTrust: {
            ...current?.inboundTrust,
            isMfaAccepted: desired.isMfaAccepted,
            isCompliantDeviceAccepted: desired.isCompliantDeviceAccepted,
            isHybridAzureADJoinedDeviceAccepted: desired.isHybridAzureADJoinedDeviceAccepted,
        },
        b2bCollaborationInbound: {
            usersAndGroups: partition(
                current?.b2bCollaborationInbound?.usersAndGroups,
                desired.b2bCollaborationInbound,
                USER_TARGET,
            ),
            applications: partition(
                current?.b2bCollaborationInbound?.applications,
                desired.b2bCollaborationInboundApps ?? desired.b2bCollaborationInbound,
                APP_TARGET,
            ),
        },
        b2bCollaborationOutbound: {
            usersAndGroups: outboundUsersPartition(
                current?.b2bCollaborationOutbound?.usersAndGroups,
                desired.b2bCollaborationOutbound,
                outboundTargets(
                    desired.b2bCollaborationOutboundAppliesTo,
                    desired.b2bCollaborationOutboundUsers,
                    desired.b2bCollaborationOutboundGroups,
                ),
            ),
            applications: partition(
                current?.b2bCollaborationOutbound?.applications,
                desired.b2bCollaborationOutboundApps ?? desired.b2bCollaborationOutbound,
                APP_TARGET,
            ),
        },
        b2bDirectConnectInbound: {
            usersAndGroups: partition(
                current?.b2bDirectConnectInbound?.usersAndGroups,
                desired.b2bDirectConnectInbound,
                USER_TARGET,
            ),
            applications: partition(
                current?.b2bDirectConnectInbound?.applications,
                desired.b2bDirectConnectInboundApps ?? desired.b2bDirectConnectInbound,
                APP_TARGET,
            ),
        },
        b2bDirectConnectOutbound: {
            usersAndGroups: outboundUsersPartition(
                current?.b2bDirectConnectOutbound?.usersAndGroups,
                desired.b2bDirectConnectOutbound,
                outboundTargets(
                    desired.b2bDirectConnectOutboundAppliesTo,
                    desired.b2bDirectConnectOutboundUsers,
                    desired.b2bDirectConnectOutboundGroups,
                ),
            ),
            applications: partition(
                current?.b2bDirectConnectOutbound?.applications,
                desired.b2bDirectConnectOutboundApps ?? desired.b2bDirectConnectOutbound,
                APP_TARGET,
            ),
        },
        tenantRestrictions: {
            devices: current?.tenantRestrictions?.devices ?? null,
            usersAndGroups: partition(
                current?.tenantRestrictions?.usersAndGroups,
                desired.tenantRestrictions ?? 'allowed',
                USER_TARGET,
            ),
            applications: partition(
                current?.tenantRestrictions?.applications,
                desired.tenantRestrictionsApps ?? desired.tenantRestrictions ?? 'allowed',
                APP_TARGET,
            ),
        },
    };

    return updateCrossTenantAccessPolicy({ _tenant, policy: updatedPolicy });
}
